import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaGavel, FaClock, FaMapMarkerAlt, FaFire, FaImage, FaTrophy, FaCheckCircle } from 'react-icons/fa';

function Announcement({ 
    name, 
    categoryName, 
    subCategoryName, 
    price, 
    id, 
    imageUrl, 
    createdAt, 
    endDate, 
    city,
    bidCount = 0,
    currentBid = null,
    viewCount = 0
}) {
    const [timeRemaining, setTimeRemaining] = useState('');
    const [isExpired, setIsExpired] = useState(false);
    const [isHot, setIsHot] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Tarix formatı - optimized
    const formatDateString = useMemo(() => {
        if (!createdAt) return "Bilinmir";
        const date = new Date(createdAt);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }, [createdAt]);

    // URL formatı
    const formatUrlString = (str) => {
        if (!str) return "";
        const azerbaijaniMap = {
            "ə": "e", "ş": "s", "ğ": "g", "ü": "u", "ö": "o", "ç": "c", "ı": "i",
            "Ə": "E", "Ş": "S", "Ğ": "G", "Ü": "U", "Ö": "O", "Ç": "C", "I": "I"
        };
        return str
            .toLowerCase()
            .replace(/[əşıöğüçƏŞİÖĞÜÇ]/g, match => azerbaijaniMap[match] || match)
            .replace(/\s+/g, "-")
            .replace(/[^\w-]+/g, "");
    };

    // Qalan vaxt hesablama
    useEffect(() => {
        if (!endDate) return;

        const calculateTimeRemaining = () => {
            const now = new Date().getTime();
            const end = new Date(endDate).getTime();
            const difference = end - now;

            if (difference <= 0) {
                setIsExpired(true);
                setTimeRemaining('Hərrac bitib');
                return;
            }

            // Son 24 saat - HOT
            if (difference <= 24 * 60 * 60 * 1000) {
                setIsHot(true);
            }

            // Son 6 saat - CRITICAL
            const isCritical = difference <= 6 * 60 * 60 * 1000;

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            if (days > 0) {
                setTimeRemaining(`${days} gün ${hours} saat`);
            } else if (hours > 0) {
                setTimeRemaining(`${hours} saat ${minutes} dəq`);
            } else if (minutes > 0) {
                setTimeRemaining(`${minutes} dəq ${seconds} san`);
            } else {
                setTimeRemaining(`${seconds} saniyə`);
            }
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [endDate]);

    // Yeni elan yoxlaması (24 saat)
    useEffect(() => {
        if (createdAt) {
            const created = new Date(createdAt).getTime();
            const now = new Date().getTime();
            const hoursSinceCreation = (now - created) / (1000 * 60 * 60);
            setIsNew(hoursSinceCreation <= 24);
        }
    }, [createdAt]);

    const formattedCategory = formatUrlString(categoryName);
    const formattedSubCategory = formatUrlString(subCategoryName);
    const displayPrice = currentBid || price;
    const hasBids = bidCount > 0;

    return (
        <Link 
            to={`/elanlar/${formattedCategory}/${formattedSubCategory}/${id}`} 
            className={`auction-card-item ${isExpired ? 'auction-card-item--expired' : ''} ${isHot ? 'auction-card-item--hot' : ''}`}
        >
            {/* Şəkil Container */}
            <div className="auction-card-img-wrap">
                {imageUrl && !imageError ? (
                    <>
                        <img 
                            src={`https://api.herracim.az/HerracImages/${imageUrl}`}
                            alt={name}
                            className={`auction-card-img ${imageLoaded ? 'auction-card-img-loaded' : ''}`}
                            onLoad={() => setImageLoaded(true)}
                            onError={() => setImageError(true)}
                            loading="lazy"
                        />
                        {!imageLoaded && (
                            <div className="auction-card-img-skeleton">
                                <FaImage />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="auction-card-no-img">
                        <FaGavel />
                        <span>Şəkil yoxdur</span>
                    </div>
                )}

                {/* Badges */}
                <div className="auction-card-badges">
                    {isNew && !isExpired && (
                        <div className="auction-card-badge auction-card-badge--new">
                            <FaCheckCircle /> YENİ
                        </div>
                    )}
                    {isHot && !isExpired && (
                        <div className="auction-card-badge auction-card-badge--hot">
                            <FaFire /> HOT
                        </div>
                    )}
                    {isExpired && (
                        <div className="auction-card-badge auction-card-badge--expired">
                            BİTİB
                        </div>
                    )}
                </div>

                {/* Timer Overlay */}
                {!isExpired && (
                    <div className={`auction-card-timer ${isHot ? 'auction-card-timer--hot' : ''}`}>
                        <FaClock />
                        <span>{timeRemaining}</span>
                    </div>
                )}

                {/* Gradient Overlay */}
                <div className="auction-card-gradient"></div>
            </div>

            {/* Content */}
            <div className="auction-card-content">
                {/* Qiymət Section */}
                <div className="auction-card-price-section">
                    <div className="auction-card-price-wrap">
                        <FaGavel className="auction-card-price-icon" />
                        <div className="auction-card-price-info">
                            <span className="auction-card-price-label">
                                {hasBids ? 'Cari təklif' : 'Başlanğıc qiymət'}
                            </span>
                            <div className="auction-card-price-amount">
                                <span className="auction-card-price-value">{displayPrice.toFixed(2)}</span>
                                <span className="auction-card-price-currency">AZN</span>
                            </div>
                        </div>
                    </div>
                    {hasBids && (
                        <div className="auction-card-bid-count">
                            <FaTrophy className="auction-card-trophy-icon" />
                            <span>{bidCount} təklif</span>
                        </div>
                    )}
                </div>

                {/* Ad */}
                <h3 className="auction-card-title">{name}</h3>

                {/* Kateqoriya */}
                <div className="auction-card-category">
                    <span className="auction-card-category-main">{categoryName}</span>
                    {subCategoryName && (
                        <>
                            <span className="auction-card-category-divider">/</span>
                            <span className="auction-card-category-sub">{subCategoryName}</span>
                        </>
                    )}
                </div>

                {/* Meta məlumat */}
                <div className="auction-card-meta">
                    <div className="auction-card-meta-item">
                        <FaMapMarkerAlt className="auction-card-meta-icon" />
                        <span>{city || "Bilinmir"}</span>
                    </div>
                    <div className="auction-card-meta-item">
                        <FaClock className="auction-card-meta-icon" />
                        <span>{formatDateString}</span>
                    </div>
                </div>
            </div>

            {/* Hover Effect */}
            <div className="auction-card-hover-overlay">
                <span className="auction-card-hover-text">
                    <FaGavel /> Ətraflı bax
                </span>
            </div>
        </Link>
    );
}

export default Announcement;